import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { fetchData } from '../../helpers/apiUtils';
import { useUrlValidation } from '../../hooks/useUrlValidation';
import { UserContext } from '../../contexts/UserContext';

interface Webinar {
  webinar_id: string;
  title: string;
  concept: string | null;
  start_date: string;
  order_url: string | null;
  missed_webinar_url: string | null;
}

interface CTALinksModalProps {
  open: boolean;
  onClose: () => void;
  webinar: Webinar | null;
  onSaved?: () => void;
}

// Bis 2026-10-28 erzeugte dieser Dialog zusaetzlich die drei StealthSeminar-CTA-Links
// (`?banner=<WBI>&utm_campaign={utm_campaign}&utm_term={utm_term}`). StealthSeminar ist seit
// 02.10. abgeschaltet; die kopierten Links landeten als statische Ziele auf den LPs, und PL-X
// speicherte deren Banner statt der echten Quelle (lp-pages-system.md § `banner`/`rdl` am Ziel).
// Geblieben ist das Speichern von Bestell- und Replay-URL.
const CTALinksModal: React.FC<CTALinksModalProps> = ({ open, onClose, webinar, onSaved }) => {
  // Die Replay-URL schreibt seit 2026-09-05 der LP-Manager; der Server lehnt das Feld fuer
  // Nicht-Admins mit 403 ab — und zwar allein dafuer, dass der Schluessel im Body steht.
  const user = useContext(UserContext);
  const isAdmin = !!user?.is_admin;

  const [orderUrl, setOrderUrl] = useState('');
  const [missedWebinarUrl, setMissedWebinarUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // URL validation
  const { validateUrl, getState, resetAll, hasUnreachableUrls, isValidating } = useUrlValidation();
  const orderUrlState = getState('order_url');
  const missedWebinarUrlState = getState('missed_webinar_url');

  // Initialize form values when modal opens
  useEffect(() => {
    if (open && webinar) {
      setOrderUrl(webinar.order_url || '');
      setMissedWebinarUrl(webinar.missed_webinar_url || '');
      setError(null);
      resetAll();
    }
  }, [open, webinar]);

  const handleClose = () => {
    setOrderUrl('');
    setMissedWebinarUrl('');
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!webinar) return;

    if (!orderUrl.trim()) {
      setError('Bestell-URL ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    // Die Replay-URL nur mitschicken, wenn ein Admin sie hier wirklich AENDERT — der Server
    // antwortet auf jedes Vorkommen des Schluessels ohne Admin mit 403 (Muster:
    // WebinarForm `urlPayload`). Vorher ging der Dialog dadurch fuer jeden Nicht-Admin ins
    // Leere, obwohl nur die Order-URL gespeichert werden sollte.
    const replayGeaendert = missedWebinarUrl.trim() !== (webinar.missed_webinar_url || '');
    const replayPayload =
      isAdmin && replayGeaendert ? { missed_webinar_url: missedWebinarUrl.trim() || null } : {};

    try {
      const saveResponse = await fetchData(
        `/api/webinar-management/webinars/${webinar.webinar_id}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            order_url: orderUrl.trim(),
            ...replayPayload,
          }),
        },
      );

      if (!saveResponse.success) {
        setError(saveResponse.error || 'Speichern fehlgeschlagen');
        return;
      }
      onSaved?.();
      handleClose();
    } catch (err) {
      console.error('Failed to save URLs:', err);
      setError('Ein Fehler ist aufgetreten');
    } finally {
      setSaving(false);
    }
  };

  if (!webinar) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bestell- und Replay-URL</DialogTitle>
      <DialogContent>
        {/* Webinar Info */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle2" color="textSecondary">
            Webinar
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            {webinar.title}
          </Typography>
          <Box mt={1} display="flex" gap={4}>
            <Box>
              <Typography variant="caption" color="textSecondary">
                ID
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                {webinar.webinar_id}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Concept
              </Typography>
              <Typography variant="body2">{webinar.concept || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Datum
              </Typography>
              <Typography variant="body2">
                {new Date(webinar.start_date).toLocaleDateString('de-DE')}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Bestell-URL (erforderlich)"
            value={orderUrl}
            onChange={(e) => setOrderUrl(e.target.value)}
            onBlur={() => validateUrl('order_url', orderUrl)}
            placeholder={`https://lp.finanztrends.de/w/${webinar.webinar_id}/bestellen/`}
            fullWidth
            required
            error={orderUrlState.reachable === false && !orderUrlState.cross_domain_redirect}
            helperText={
              orderUrlState.validating
                ? 'URL wird geprüft …'
                : orderUrlState.reachable === true
                  ? 'URL erreichbar'
                  : orderUrlState.cross_domain_redirect
                    ? `Weiterleitung zu anderer Domain: ${orderUrlState.redirect_url}`
                    : orderUrlState.error
                      ? orderUrlState.error
                      : 'Die URL zur Bestellseite, ohne Banner und ohne Platzhalter'
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {orderUrlState.validating && <CircularProgress size={18} />}
                  {orderUrlState.reachable === true && !orderUrlState.cross_domain_redirect && (
                    <CheckCircleIcon color="success" fontSize="small" />
                  )}
                  {orderUrlState.cross_domain_redirect && (
                    <WarningIcon sx={{ color: '#ed6c02' }} fontSize="small" />
                  )}
                  {orderUrlState.reachable === false && !orderUrlState.cross_domain_redirect && (
                    <ErrorIcon color="error" fontSize="small" />
                  )}
                </InputAdornment>
              ),
            }}
          />
          {/* Ohne Admin nur ANZEIGEN: das Feld wird nicht mitgeschickt, also auch nicht
              geprueft — ein `reachable: false` wuerde sonst ueber `hasUnreachableUrls()`
              den Speichern-Knopf sperren, ohne dass es hier eine Handhabe dagegen gaebe. */}
          {!isAdmin ? (
            <TextField
              label="Replay-URL (Aufzeichnung)"
              value={missedWebinarUrl}
              fullWidth
              InputProps={{ readOnly: true }}
              helperText="Setzt der Webinar-LP-Manager beim Publizieren + Umschalten."
            />
          ) : (
            <TextField
              label="Replay-URL (optional)"
              value={missedWebinarUrl}
              onChange={(e) => setMissedWebinarUrl(e.target.value)}
              onBlur={() => validateUrl('missed_webinar_url', missedWebinarUrl)}
              placeholder={`https://lp.finanztrends.de/w/${webinar.webinar_id}/aufzeichnung/`}
              fullWidth
              error={
                missedWebinarUrlState.reachable === false &&
                !missedWebinarUrlState.cross_domain_redirect
              }
              helperText={
                missedWebinarUrlState.validating
                  ? 'URL wird geprüft …'
                  : missedWebinarUrlState.reachable === true
                    ? 'URL erreichbar'
                    : missedWebinarUrlState.cross_domain_redirect
                      ? `Weiterleitung zu anderer Domain: ${missedWebinarUrlState.redirect_url}`
                      : missedWebinarUrlState.error
                        ? missedWebinarUrlState.error
                        : 'Ziel für Anmelder, die das Webinar verpasst haben (leer = Bestell-URL)'
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {missedWebinarUrlState.validating && <CircularProgress size={18} />}
                    {missedWebinarUrlState.reachable === true &&
                      !missedWebinarUrlState.cross_domain_redirect && (
                        <CheckCircleIcon color="success" fontSize="small" />
                      )}
                    {missedWebinarUrlState.cross_domain_redirect && (
                      <WarningIcon sx={{ color: '#ed6c02' }} fontSize="small" />
                    )}
                    {missedWebinarUrlState.reachable === false &&
                      !missedWebinarUrlState.cross_domain_redirect && (
                        <ErrorIcon color="error" fontSize="small" />
                      )}
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Schließen</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !orderUrl.trim() || hasUnreachableUrls() || isValidating()}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Speichere …' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CTALinksModal;
